import React, {useCallback, useEffect, useState} from 'react';
import './App.css';
import Layout from "@/components/Layout";
import {Article, getArticlePage, getFileUrl, getSlideshow, getWebsiteInfo, Slideshow, WebsiteInfo} from "@/api/api";
import styles from '@/styles/home.module.css';
import ContentBlock from "@/components/ContentBlock";
import CertificateSearch from "@/components/CertificateSearch";
import {FriendshipLink} from "@/components/FriendshipLink";
import { Link } from 'react-router-dom';

function App() {
    const [websiteInfo, setWebsiteInfo] = useState<WebsiteInfo>();
    const [slideshows, setSlideshows] = useState<Slideshow[]>([]);
    const [newsArticles, setNewsArticles] = useState<Article[]>([]);
    const [skillsArticles, setSkillsArticles] = useState<Article[]>([]);
    const [policyArticles, setPolicyArticles] = useState<Article[]>([]);
    const [educationArticles, setEducationArticles] = useState<Article[]>([]);
    const [policiesArticles, setPoliciesArticles] = useState<Article[]>([]);
    const [decisionsArticles, setDecisionsArticles] = useState<Article[]>([]);
    const [researchArticles, setResearchArticles] = useState<Article[]>([]);
    const [headlineArticle, setHeadlineArticle] = useState<Article | null>(null);
    const [selectedNewsIndex, setSelectedNewsIndex] = useState(0);
    const [loadingWebsiteInfo, setLoadingWebsiteInfo] = useState(true);
    const [loadingSlideshow, setLoadingSlideshow] = useState(true);
    const [loadingArticles, setLoadingArticles] = useState(true);
    const [currentSlide, setCurrentSlide] = useState(0);

    // Fetch website info
    useEffect(() => {
        const fetchWebsiteInfo = async () => {
            try {
                setLoadingWebsiteInfo(true);
                const response = await getWebsiteInfo();
                if (response.data.code === 1) {
                    setWebsiteInfo(response.data.data);
                }
            } catch (error) {
                console.error('Error fetching website info:', error);
            } finally {
                setLoadingWebsiteInfo(false);
            }
        };

        fetchWebsiteInfo();
    }, []);

    // Fetch slideshow images
    useEffect(() => {
        const fetchSlideshow = async () => {
            try {
                setLoadingSlideshow(true);
                const response = await getSlideshow();
                if (response.data.code === 1) {
                    setSlideshows(response.data.data);
                }
            } catch (error) {
                console.error('Error fetching slideshow images:', error);
            } finally {
                setLoadingSlideshow(false);
            }
        };

        fetchSlideshow();
    }, []);

    // Fetch articles for all sections
    useEffect(() => {
        const fetchAllArticles = async () => {
            try {
                setLoadingArticles(true);

                // Fetch news articles (通知公告)
                const newsResponse = await getArticlePage(1, 10, '新闻中心', '通知公告');
                if (newsResponse.data.code === 1) {
                    setNewsArticles(newsResponse.data.data.content);
                }

                // Fetch skills articles
                const skillsResponse = await getArticlePage(1, 10, '职业技能', '');
                if (skillsResponse.data.code === 1) {
                    setSkillsArticles(skillsResponse.data.data.content);
                }

                // Fetch policy articles (政策解读)
                const policyResponse = await getArticlePage(1, 7, '研究动态', '政策解读');
                if (policyResponse.data.code === 1) {
                    setPolicyArticles(policyResponse.data.data.content);
                }

                // Fetch education articles (教育动态)
                const educationResponse = await getArticlePage(1, 7, '研究动态', '教育动态');
                if (educationResponse.data.code === 1) {
                    setEducationArticles(educationResponse.data.data.content);
                }

                // Fetch policies articles (政策法规)
                const policiesResponse = await getArticlePage(1, 7, '政策法规', '');
                if (policiesResponse.data.code === 1) {
                    setPoliciesArticles(policiesResponse.data.data.content);
                }

                // Fetch decisions articles (决策参考)
                const decisionsResponse = await getArticlePage(1, 7, '新闻中心', '决策参考');
                if (decisionsResponse.data.code === 1) {
                    setDecisionsArticles(decisionsResponse.data.data.content);
                }

                // Fetch research articles (调查研究)
                const researchResponse = await getArticlePage(1, 7, '新闻中心', '调查研究');
                if (researchResponse.data.code === 1) {
                    setResearchArticles(researchResponse.data.data.content);
                }

                // Fetch headline article (今日头条)
                const headlineResponse = await getArticlePage(1, 1, '研究动态', '今日头条');
                if (headlineResponse.data.code === 1 && headlineResponse.data.data.content.length > 0) {
                    setHeadlineArticle(headlineResponse.data.data.content[0]);
                }
            } catch (error) {
                console.error('Error fetching articles:', error);
            } finally {
                setLoadingArticles(false);
            }
        };

        fetchAllArticles();
    }, []);

    // Auto-advance slideshow
    useEffect(() => {
        if (slideshows.length > 0) {
            const interval = setInterval(() => {
                setCurrentSlide(prevSlide => (prevSlide + 1) % slideshows.length);
            }, 5000); // Change slide every 5 seconds

            return () => clearInterval(interval);
        }
    }, [slideshows.length]);

    // Handle tab selection
    const handleTabClick = useCallback((index: number) => {
        setSelectedNewsIndex(index);
    }, []);

    // Format date from ISO string to MM-DD format
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${month}-${day}`;
    };

    // Handle slideshow navigation
    const goToSlide = (index: number) => {
        setCurrentSlide(index);
    };
    return (
        <Layout>
            <div className={styles.contentContainerWrapper}>
                <div className={styles.partContainer}>
                    <div className={styles.carouselNewsRow}>
                        {/* Image Carousel Start */}
                        <div className={styles.indnews}>
                            <div className={styles.newsPic}>
                                <div className={styles.carousel}>
                                    <div
                                        className={styles.carouselInner}
                                        style={{transform: `translateX(-${currentSlide * 100}%)`}}
                                    >
                                        {loadingSlideshow ? (
                                            <div className={styles.slide}>加载中...</div>
                                        ) : slideshows.length > 0 ? (
                                            slideshows.map((slide) => (
                                                <div key={slide.id} className={styles.slide}>
                                                    <Link to={`/`} title={`${slide.number}`}>
                                                        <img
                                                            src={getFileUrl(slide.pic)}
                                                            alt={`轮播图 ${slide.number}`}
                                                        />
                                                    </Link>
                                                </div>
                                            ))
                                        ) : (
                                            <div className={styles.slide}>
                                                <Link to="/" title="暂无轮播图">
                                                    <img
                                                        src="/static/picture/1688347236132167.jpg"
                                                        alt="暂无轮播图"
                                                    />
                                                </Link>
                                            </div>
                                        )}
                                    </div>

                                    {/* Slideshow dots navigation */}
                                    {!loadingSlideshow && slideshows.length > 0 && (
                                        <div className={styles.carouselDots}>
                                            {slideshows.map((_, index) => (
                                                <button
                                                    key={index}
                                                    className={`${styles.carouselDot} ${currentSlide === index ? styles.active : ''}`}
                                                    onClick={() => goToSlide(index)}
                                                    aria-label={`Slide ${index + 1}`}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        {/* Image Carousel End */}

                        {/* Page Start */}
                        <div className={styles.newsTxt} id="news">
                            {/* Page Categories Start */}
                            <div className={styles.newsTitle}>
                                <div className={styles.tabNav}>
                                    <button
                                        className={`${styles.tabButton} ${selectedNewsIndex === 0 ? styles.current : ''}`}
                                        onClick={() => handleTabClick(0)}
                                    >
                                        通知公告
                                    </button>
                                    <button
                                        className={`${styles.tabButton} ${selectedNewsIndex === 1 ? styles.current : ''}`}
                                        onClick={() => handleTabClick(1)}
                                    >
                                        职业技能
                                    </button>
                                </div>
                                <Link to="/news/notices" className={styles.more}>更多 &gt;&gt;</Link>
                            </div>
                            {/* Page Categories End */}

                            <div className={styles.tabContent}>
                                <div className={styles.tabPanel}
                                     style={{display: selectedNewsIndex === 0 ? 'block' : 'none'}}>
                                    <ul className={styles.newsList}>
                                        {loadingArticles ? (
                                            <li>加载中...</li>
                                        ) : newsArticles.length > 0 ? (
                                            newsArticles.map((article) => (
                                                <li key={article.id}>
                                                    <Link
                                                        to={`/news/notices/${article.id}`}
                                                        title={article.title}
                                                    >
                                                        {article.title}
                                                    </Link>
                                                    <span>{formatDate(article.createTime)}</span>
                                                </li>
                                            ))
                                        ) : (
                                            <li>暂无通知公告</li>
                                        )}
                                    </ul>
                                </div>
                                <div className={styles.tabPanel}
                                     style={{display: selectedNewsIndex === 1 ? 'block' : 'none'}}>
                                    <ul className={styles.newsList}>
                                        {loadingArticles ? (
                                            <li>加载中...</li>
                                        ) : skillsArticles.length > 0 ? (
                                            skillsArticles.map((article) => (
                                                <li key={article.id}>
                                                    <Link
                                                        to={`/skills/${article.id}`}
                                                        title={article.title}
                                                    >
                                                        {article.title}
                                                    </Link>
                                                    <span>{formatDate(article.createTime)}</span>
                                                </li>
                                            ))
                                        ) : (
                                            <li>暂无职业技能文章</li>
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </div>
                        {/* Page End */}
                    </div>
                </div>


                <div className={styles.partContainer}>
                    <div className={styles.ttRow}>
                        <div className={styles.tt_title}>今日头条</div>
                        <div className={styles.tt_container}>
                            {loadingArticles ? (
                                <div>加载中...</div>
                            ) : headlineArticle ? (
                                <>
                                    <div className={styles.ttt}>
                                        <Link
                                            to={`/research/headline/${headlineArticle.id}`}
                                            title={headlineArticle.title}
                                        >
                                            {headlineArticle.title}
                                        </Link>
                                    </div>
                                    <div className={styles.ttp}>
                                        {headlineArticle.jsonStr
                                            ? headlineArticle.jsonStr.replace(/<\/?[^>]+(>|$)/g, "").substring(0, 150) + '...'
                                            : '内容加载中...'}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className={styles.ttt}>
                                        <Link
                                            to="/news/notices/84"
                                            title="城市与人才共发展，绘就中国式现代化美好画卷"
                                        >
                                            城市与人才共发展，绘就中国式现代化美好画卷
                                        </Link>
                                    </div>
                                    <div className={styles.ttp}>
                                        城因人才兴，才因城而幸。城市与人才共发展是推动城市高质量发展的关键。党的二十大报告指出，"深入实施人才强国战略""坚持为党育人、为国育才，全面提高人才自主培养质量，着力造就拔尖创新···
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {
                loadingWebsiteInfo ? (
                    <div>图片加载中...</div>
                ) : (
                    <div className={styles.container_img}>
                        <img src={getFileUrl(websiteInfo?.bigPic1)} alt=""/>
                    </div>
                )
            }

            <div className={styles.content_container}>
                <ContentBlock
                    title="政策解读"
                    linkPath="/research/policy"
                    articles={loadingArticles ? [] : policyArticles}
                />
                <ContentBlock
                    title="教育动态"
                    linkPath="/research/education"
                    articles={loadingArticles ? [] : educationArticles}
                />
                <CertificateSearch/>
            </div>

            {
                loadingWebsiteInfo ? (
                    <div>图片加载中...</div>
                ) : (
                    <div className={styles.container_img}>
                        <img src={getFileUrl(websiteInfo?.bigPic2)} alt=""/>
                    </div>
                )
            }

            <div className={styles.content_container}>
                <ContentBlock
                    title="政策法规"
                    linkPath="/policies"
                    articles={loadingArticles ? [] : policiesArticles}
                />
                <ContentBlock
                    title="决策参考"
                    linkPath="/news/decisions"
                    articles={loadingArticles ? [] : decisionsArticles}
                />
                <ContentBlock
                    title="调查研究"
                    linkPath="/news/research"
                    articles={loadingArticles ? [] : researchArticles}
                />
            </div>

            <div className={styles.content_container}>
                <FriendshipLink/>
            </div>
        </Layout>
    );
}

export default App;
